import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Space,
  Switch,
  Table,
  Tag,
  Upload,
  message,
} from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import type { UploadFile, UploadProps } from "antd";
import { get, post } from "../../utils/http";

interface ModuleData {
  id: number;
  module_name: string;
  module_name_en: string;
  module_desc: string;
  list_pic: string;
  detail_pic: string;
  is_active: boolean;
  price_cents: number;
  original_price_cents?: number | null;
  is_for_sale: boolean;
  ios_product_id_permanent_unlock?: string;
  create_time: string;
  update_time: string;
}

interface PaymentConfigData {
  all_access_product: {
    price_cents: number;
    original_price_cents?: number | null;
    ios_product_id?: string | null;
    is_for_sale: boolean;
  };
}

const fenToYuan = (value?: number | null) =>
  typeof value === "number" ? (value / 100).toFixed(2) : "-";

const ModulePage: React.FC = () => {
  const [modules, setModules] = useState<ModuleData[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [paymentConfigLoading, setPaymentConfigLoading] = useState(false);
  const [moduleForm] = Form.useForm();
  const [paymentConfigForm] = Form.useForm();
  const [editingModule, setEditingModule] = useState<ModuleData | null>(null);
  const [listPicFileList, setListPicFileList] = useState<UploadFile[]>([]);
  const [detailPicFileList, setDetailPicFileList] = useState<UploadFile[]>([]);

  const customUpload = async (options: any) => {
    const { file, onSuccess, onError, onProgress } = options;
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await post("/api/manage/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent: any) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
          onProgress({ percent: percentCompleted });
        },
      });

      if (response.data?.data?.url) {
        onSuccess(response.data.data);
        message.success("上传成功");
        return;
      }

      throw new Error("上传失败");
    } catch (error) {
      onError(error);
      message.error("上传失败");
    }
  };

  const handleListPicChange: UploadProps["onChange"] = ({ fileList }) => {
    setListPicFileList(fileList);
    if (fileList.length > 0 && fileList[0].status === "done") {
      moduleForm.setFieldValue(
        "list_pic",
        fileList[0].url || fileList[0].response?.url,
      );
    } else {
      moduleForm.setFieldValue("list_pic", "");
    }
  };

  const handleDetailPicChange: UploadProps["onChange"] = ({ fileList }) => {
    setDetailPicFileList(fileList);
    if (fileList.length > 0 && fileList[0].status === "done") {
      moduleForm.setFieldValue(
        "detail_pic",
        fileList[0].url || fileList[0].response?.url,
      );
    } else {
      moduleForm.setFieldValue("detail_pic", "");
    }
  };

  const fetchModules = useCallback(async () => {
    setLoading(true);
    try {
      const res = await get("/api/manage/module-list");
      if (res.data.code === 0) {
        setModules(Array.isArray(res.data.data.list) ? res.data.data.list : []);
      }
    } catch (error) {
      message.error("获取模块列表失败");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPaymentConfig = useCallback(async () => {
    setPaymentConfigLoading(true);
    try {
      const res = await get("/api/manage/payment-config");
      if (res.data.code === 0) {
        const config = res.data.data as PaymentConfigData;
        paymentConfigForm.setFieldsValue({
          all_access_price_cents: config.all_access_product?.price_cents ?? 0,
          all_access_original_price_cents:
            config.all_access_product?.original_price_cents ?? null,
          all_access_ios_product_id:
            config.all_access_product?.ios_product_id ?? "",
        });
      }
    } catch (error) {
      message.error("获取全站支付配置失败");
    } finally {
      setPaymentConfigLoading(false);
    }
  }, [paymentConfigForm]);

  useEffect(() => {
    fetchModules();
    fetchPaymentConfig();
  }, [fetchModules, fetchPaymentConfig]);

  const resetModuleModal = () => {
    setModalVisible(false);
    setEditingModule(null);
    moduleForm.resetFields();
    setListPicFileList([]);
    setDetailPicFileList([]);
  };

  const handleSaveModule = async (values: any) => {
    try {
      const payload = {
        ...values,
        original_price_cents: values.original_price_cents ?? null,
      };

      const res = editingModule
        ? await post("/api/manage/module-update", {
            id: editingModule.id,
            ...payload,
          })
        : await post("/api/manage/module-create", payload);

      if (res.data.code === 0) {
        message.success(editingModule ? "编辑模块成功" : "创建模块成功");
        resetModuleModal();
        fetchModules();
      }
    } catch (error) {
      message.error(editingModule ? "编辑模块失败" : "创建模块失败");
    }
  };

  const handleEdit = (record: ModuleData) => {
    setEditingModule(record);
    moduleForm.setFieldsValue({
      module_name: record.module_name,
      module_name_en: record.module_name_en,
      module_desc: record.module_desc,
      list_pic: record.list_pic,
      detail_pic: record.detail_pic,
      price_cents: record.price_cents,
      original_price_cents: record.original_price_cents,
      is_for_sale: record.is_for_sale,
      ios_product_id_permanent_unlock: record.ios_product_id_permanent_unlock,
    });

    setListPicFileList(
      record.list_pic
        ? [
            {
              uid: "-1",
              name: "分类列表图",
              status: "done",
              url: record.list_pic,
            },
          ]
        : [],
    );

    setDetailPicFileList(
      record.detail_pic
        ? [
            {
              uid: "-1",
              name: "分类详情图",
              status: "done",
              url: record.detail_pic,
            },
          ]
        : [],
    );

    setModalVisible(true);
  };

  const handleDelete = (id: number) => {
    Modal.confirm({
      title: "确认删除",
      content: "确定要删除该模块吗？",
      onOk: async () => {
        try {
          const res = await post("/api/manage/module-delete", { id });
          if (res.data.code === 0) {
            message.success("删除模块成功");
            fetchModules();
          }
        } catch (error) {
          message.error("删除模块失败");
        }
      },
    });
  };

  const handleToggleStatus = async (id: number, isActive: boolean) => {
    try {
      const res = await post("/api/manage/module-toggle-status", {
        id,
        is_active: isActive,
      });
      if (res.data.code === 0) {
        message.success(isActive ? "上架成功" : "下架成功");
        fetchModules();
      }
    } catch (error) {
      message.error("操作失败");
    }
  };

  const handleSavePaymentConfig = async () => {
    try {
      const values = await paymentConfigForm.validateFields();
      const res = await post("/api/manage/payment-config", values);
      if (res.data.code === 0) {
        message.success("全站支付配置已保存");
        fetchPaymentConfig();
      }
    } catch (error) {
      message.error("保存全站支付配置失败");
    }
  };

  const columns = [
    {
      title: "模块名称",
      dataIndex: "module_name",
      key: "module_name",
      width: 180,
      fixed: "left" as const,
    },
    {
      title: "翻译",
      dataIndex: "module_name_en",
      key: "module_name_en",
      width: 180,
    },
    {
      title: "售价",
      key: "price_cents",
      width: 140,
      render: (_: any, record: ModuleData) => (
        <Space direction="vertical" size={0}>
          <span>¥{fenToYuan(record.price_cents)}</span>
          <span style={{ color: "#999", fontSize: 12 }}>
            原价: ¥{fenToYuan(record.original_price_cents)}
          </span>
        </Space>
      ),
    },
    {
      title: "售卖状态",
      key: "sale_status",
      width: 120,
      render: (_: any, record: ModuleData) =>
        record.is_for_sale ? (
          <Tag color="green">可售卖</Tag>
        ) : (
          <Tag>未售卖</Tag>
        ),
    },
    {
      title: "解锁规则",
      key: "unlock_rule",
      width: 140,
      render: () => <Tag color="gold">永久解锁</Tag>,
    },
    {
      title: "更新时间",
      dataIndex: "update_time",
      key: "update_time",
      width: 180,
      render: (text: string) =>
        text
          ? new Date(text).toLocaleString("zh-CN", {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
              hour12: false,
            })
          : "-",
    },
    {
      title: "上架",
      dataIndex: "is_active",
      key: "is_active",
      width: 100,
      render: (isActive: boolean, record: ModuleData) => (
        <Switch
          checked={isActive}
          checkedChildren="上架"
          unCheckedChildren="下架"
          onChange={(checked) => handleToggleStatus(record.id, checked)}
        />
      ),
    },
    {
      title: "操作",
      key: "action",
      width: 160,
      render: (_: any, record: ModuleData) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 20 }}>
      <Space direction="vertical" size={16} style={{ display: "flex" }}>
        <Card title="全站永久解锁商品" loading={paymentConfigLoading}>
          <Form form={paymentConfigForm} layout="vertical">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  label="售价（分）"
                  name="all_access_price_cents"
                  rules={[{ required: true, message: "请输入售价" }]}
                >
                  <InputNumber min={0} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="原价（分）"
                  name="all_access_original_price_cents"
                >
                  <InputNumber min={0} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="iOS IAP 商品ID"
                  name="all_access_ios_product_id"
                >
                  <Input placeholder="例如 com.xxx.all_access_forever" />
                </Form.Item>
              </Col>
            </Row>
            <Button type="primary" onClick={handleSavePaymentConfig}>
              保存全站商品配置
            </Button>
          </Form>
        </Card>

        <Card
          title="模块管理"
          extra={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingModule(null);
                moduleForm.resetFields();
                moduleForm.setFieldsValue({
                  price_cents: 0,
                  original_price_cents: null,
                  is_for_sale: false,
                });
                setListPicFileList([]);
                setDetailPicFileList([]);
                setModalVisible(true);
              }}
            >
              新建模块
            </Button>
          }
        >
          <Table
            loading={loading}
            dataSource={modules}
            columns={columns}
            rowKey="id"
            scroll={{ x: 1480 }}
          />
        </Card>
      </Space>

      <Modal
        title={editingModule ? "编辑模块" : "新建模块"}
        open={modalVisible}
        onCancel={resetModuleModal}
        onOk={() => moduleForm.submit()}
        width={880}
        okText="保存"
      >
        <Form form={moduleForm} layout="vertical" onFinish={handleSaveModule}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="模块名称"
                name="module_name"
                rules={[{ required: true, message: "请输入模块名称" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="模块英文名" name="module_name_en">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="模块推荐语" name="module_desc">
            <Input.TextArea rows={2} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="售价（分）"
                name="price_cents"
                rules={[{ required: true, message: "请输入售价" }]}
              >
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="原价（分）" name="original_price_cents">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="是否可售"
                name="is_for_sale"
                valuePropName="checked"
              >
                <Switch checkedChildren="可售" unCheckedChildren="关闭" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="iOS 永久商品ID"
                name="ios_product_id_permanent_unlock"
              >
                <Input placeholder="例如 com.xxx.module.forever" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="列表图" name="list_pic">
            <Upload
              customRequest={customUpload}
              fileList={listPicFileList}
              onChange={handleListPicChange}
              listType="picture-card"
              maxCount={1}
            >
              {listPicFileList.length >= 1 ? null : <div>上传</div>}
            </Upload>
          </Form.Item>

          <Form.Item label="详情图" name="detail_pic">
            <Upload
              customRequest={customUpload}
              fileList={detailPicFileList}
              onChange={handleDetailPicChange}
              listType="picture-card"
              maxCount={1}
            >
              {detailPicFileList.length >= 1 ? null : <div>上传</div>}
            </Upload>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ModulePage;
